export type MetasearchFeedStatus = "PENDING" | "ACTIVE" | "SUSPENDED" | "ERROR";
export type BookingEngineType = "MEWS" | "CLOUDBEDS" | "RESNEXUS" | "AUTUMN" | "CUSTOM";
export type MetasearchTaxStrategy = "GROSS" | "NET_PLUS_RULES";
export type TaxPolicyType = "PERCENTAGE" | "FIXED_PER_NIGHT" | "FIXED_PER_STAY" | "FIXED_PER_PERSON";
export type RestrictionConditionType = "Stay" | "Start" | "End";
export type IconApprovalState = "NEW" | "APPROVED" | "REJECTED";

export type FeedStatus = {
  configId: string;
  userId: string;
  hotelId: string;
  hotelName: string;
  googlePropertyId: string;
  feedEnabled: boolean;
  feedStatus: MetasearchFeedStatus;
  bookingEngineType: BookingEngineType;
  bookingEngineBaseUrl: string;
  bookingEngineConfig?: Record<string, any> | null;
  hotelListSynced: boolean;
  lastHotelListSync: string | null;
  lastAriSync: string | null;
  lastAriSuccess: string | null;
  roomRateMappingCount: number;
  taxPolicyCount: number;
  brandId?: string | null;
};

export type AvailableHotel = {
  hotelId: string;
  hotelName: string;
  city: string;
  pmsType: string;
  currencyCode: string;
  state: string;
};

export type TaxPolicyPayload = {
  name: string;
  type: TaxPolicyType;
  amount: number;
  isTax?: boolean;
  appliesToBase?: boolean;
  isRefundable?: boolean;
};

export type RoomRateMappingPayload = {
  pmsRoomTypeId: string;
  googleRoomTypeId: string;
  pmsRateId: string;
  googleRateId: string;
  baseOccupancy?: number;
  maxOccupancy?: number;
};

export type OnboardHotelPayload = {
  hotelId: string;
  bookingEngineBaseUrl: string;
  bookingEngineType?: BookingEngineType;
  bookingEngineConfig?: Record<string, any> | null;
  checkInTime?: string;
  checkOutTime?: string;
};

export type UpdateBookingEnginePayload = {
  bookingEngineBaseUrl?: string;
  bookingEngineType?: BookingEngineType;
  bookingEngineConfig?: Record<string, any> | null;
};

export type BrandingIcon = {
  name: string;
  state: IconApprovalState;
  iconUri?: string | null;
  reference?: string;
  disapprovalReasons?: string[];
};

export type UploadIconPayload = {
  imageBase64: string;
};

export type BrandDisplayNameEntry = {
  text: string;
  languageCode?: string;
};

export type Brand = {
  name: string;
  displayNames: BrandDisplayNameEntry[];
  submittedDisplayNames?: BrandDisplayNameEntry[];
  activeDisplayNames?: BrandDisplayNameEntry[];
  displayNameState?: string;
  displayNameDisapprovalReasons?: string[];
  icon?: string | null;
  submittedIcon?: string | null;
  activeIcon?: string | null;
  activeIconUri?: string | null;
  iconState?: string;
  iconDisapprovalReasons?: string[];
  propertyCount: number;
};

export type CreateBrandPayload = {
  brandId: string;
  displayName: string;
  language?: string;
  iconId?: string;
};

export type UpdateBrandPayload = {
  displayName?: string;
  language?: string;
  iconId?: string;
};

export type BrandSetupPayload = {
  imageBase64: string;
  displayName?: string;
};

export type BrandSetupResponse = {
  brandId: string;
  iconName: string;
  iconState: string;
  brandCreated: boolean;
};

export type HotelCenterStatus = {
  partnerHotelId: string;
  matchStatus: string;
  liveOnGoogleStatus: string;
  dataIssueDetail: string[];
} | null;

export type VerifyPropertyResult = {
  matched: boolean;
  liveOnGoogle: boolean;
  matchStatus: string | undefined;
  liveOnGoogleStatus: string | undefined;
  dataIssues: string[];
  hotelView?: Record<string, any>;
};

export type Restriction = {
  id: string;
  hotelConfigId: string;
  pmsRateId: string;
  conditionType: RestrictionConditionType;
  startDate: string | null;
  endDate: string | null;
  daysOfWeek: string[] | null;
  minLengthOfStay: number | null;
  maxLengthOfStay: number | null;
  minAdvanceDays: number | null;
  maxAdvanceDays: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type PriceAccuracyScore = "EXCELLENT" | "GOOD" | "FAIR" | "POOR";

export type DiagnosticsPriceAccuracy = {
  currentScore: PriceAccuracyScore;
  currentOverallScore: PriceAccuracyScore;
  currentWebsiteScore: PriceAccuracyScore;
  currentBookOnGoogleScore: PriceAccuracyScore;
  predictedScore: PriceAccuracyScore;
  predictedOverallScore: PriceAccuracyScore;
  predictedWebsiteScore: PriceAccuracyScore;
  predictedBookOnGoogleScore: PriceAccuracyScore;
  updateTime: string;
};

export type PriceCoverageBucket = {
  advanceBookingWindowRange: string;
  lengthOfStayRange: string;
  availablePriceCount: string;
  priceCoveragePercent: number;
};

export type DiagnosticsPriceCoverage = {
  calculationDate?: { year: number; month: number; day: number };
  matchedPropertyCount?: number;
  priceCoverageBinaryPercent?: number;
  priceCoveragePercent?: number;
  priceCoverageBuckets?: PriceCoverageBucket[];
};

export type NoPriceCountDetails = {
  livePricingNotAvailableCount: number;
  livePricingTaxConfigIssueCount: number;
  livePricingOtherReasonCount: number;
};

export type PriceMissingCountDetails = {
  cacheRateMissingCount: number;
  itineraryBlockedCount: number;
};

export type MissedParticipation = {
  priceProblemCount: number;
  noLandingPageCount: number;
  priceMissingCount: number;
  priceUnavailableCount: number;
  noPriceCount: number;
  otherReasonCount: number;
  noPriceCountDetails?: NoPriceCountDetails;
  priceMissingCountDetails?: PriceMissingCountDetails;
};

export type DiagnosticsHotelResult = {
  hotelName: string;
  googlePropertyId: string;
  googleHotelId: string;
  matchStatus: string;
  liveOnGoogle: boolean;
  liveOnGoogleStatus?: string;
  matchFailureReasons: string[] | null;
  dataIssues: string[];
  participationPercent: number | null;
  opportunityCount: number | null;
  participationCount: number | null;
  missedParticipationCount: number | null;
  missedParticipation: MissedParticipation | null;
};

export type DiagnosticsSnapshot = {
  id: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  totalConfigs: number;
  averageParticipationPercent: number | null;
  priceAccuracy: DiagnosticsPriceAccuracy | null;
  priceCoverage: DiagnosticsPriceCoverage | null;
  hotelResults: DiagnosticsHotelResult[];
};

export const FEED_STATUS_COLORS: Record<MetasearchFeedStatus, string> = {
  PENDING: "bg-yellow-100 text-yellow-800 border-yellow-300",
  ACTIVE: "bg-green-100 text-green-800 border-green-300",
  SUSPENDED: "bg-orange-100 text-orange-800 border-orange-300",
  ERROR: "bg-red-100 text-red-800 border-red-300",
};

export const FEED_STATUS_LABELS: Record<MetasearchFeedStatus, string> = {
  PENDING: "Pending",
  ACTIVE: "Active",
  SUSPENDED: "Suspended",
  ERROR: "Error",
};

export const BOOKING_ENGINE_OPTIONS: { value: BookingEngineType; label: string }[] = [
  { value: "MEWS", label: "Mews" },
  { value: "CLOUDBEDS", label: "Cloudbeds" },
  { value: "RESNEXUS", label: "ResNexus" },
  { value: "AUTUMN", label: "Autumn" },
  { value: "CUSTOM", label: "Custom" },
];

export const TAX_STRATEGY_OPTIONS: { value: MetasearchTaxStrategy; label: string }[] = [
  { value: "GROSS", label: "Gross" },
  { value: "NET_PLUS_RULES", label: "Net + Rules" },
];

export const TAX_POLICY_TYPE_OPTIONS: { value: TaxPolicyType; label: string }[] = [
  { value: "PERCENTAGE", label: "Percentage" },
  { value: "FIXED_PER_NIGHT", label: "Fixed per Night" },
  { value: "FIXED_PER_STAY", label: "Fixed per Stay" },
  { value: "FIXED_PER_PERSON", label: "Fixed per Person" },
];

export const PMS_TO_ENGINE: Record<string, BookingEngineType> = {
  mews: "MEWS",
  cloudbeds: "CLOUDBEDS",
  resnexus: "RESNEXUS",
};

export const ENGINE_URL_PLACEHOLDERS: Record<BookingEngineType, string> = {
  MEWS: "https://property.mews.com/distributor/...",
  CLOUDBEDS: "https://hotels.cloudbeds.com/reservation/...",
  RESNEXUS: "https://app.resnexus.com/resnexus/reservations/...",
  AUTUMN: "https://book.yourhotel.com/...",
  CUSTOM: "https://your-booking-engine.com/...",
};

export const ICON_STATE_COLORS: Record<IconApprovalState, string> = {
  NEW: "bg-blue-100 text-blue-800 border-blue-300",
  APPROVED: "bg-green-100 text-green-800 border-green-300",
  REJECTED: "bg-red-100 text-red-800 border-red-300",
};

export const BRAND_REVIEW_STATE_COLORS: Record<string, string> = {
  NEW: "bg-blue-100 text-blue-800 border-blue-300",
  REVIEW_STATE_NEW: "bg-blue-100 text-blue-800 border-blue-300",
  APPROVED: "bg-green-100 text-green-800 border-green-300",
  REJECTED: "bg-red-100 text-red-800 border-red-300",
};
